import React, { useState } from "react";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { toast } from "react-toastify";
import { useFieldsScreenVM } from "@screens/FieldsScreen/FieldsScreenVM";
import { useLang } from "@src/hooks/useLang";

/**
 * Chrome for the custom layer on screen: what it is, where it came from, and
 * the only management action that belongs on the map (removing it). Adding is
 * reached from the layer dropdown, so it is not repeated here.
 *
 * Sits below the legend, which is anchored top-right.
 */
/**
 * Top-right, in the same column as the legend and level with the View dropdown
 * — not stranded at the bottom of the map where it collided with the support
 * chat bubble. The legend drops below it via MapYieldLegend's `topOffset`, so
 * PANEL_HEIGHT here and that offset have to stay in step.
 */
export const CUSTOM_PANEL_HEIGHT = 78;

const Root = styled.div`
  position: absolute;
  inset: 18px max(52px, env(safe-area-inset-right, 52px)) auto auto;
  box-sizing: border-box;
  height: ${CUSTOM_PANEL_HEIGHT}px;
  width: 184px;
  background: #fff;
  padding: 8px 12px;
  border-radius: 6px;
  z-index: 3;
  box-shadow: 0 2px 10px rgba(73, 76, 91, 0.14);
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const Name = styled.div`
  font-family: Nunito Sans, sans-serif;
  font-weight: 700;
  font-size: 13px;
  color: #494c5b;
  /* The panel is a fixed height so the legend can sit beneath it — a long
     John Deere filename has to clip rather than push the layout around. */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const NameInput = styled.input`
  font-family: Nunito Sans, sans-serif;
  font-weight: 700;
  font-size: 13px;
  color: #494c5b;
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #cbd5d8;
  border-radius: 4px;
  padding: 1px 4px;
  outline: none;
  :focus {
    border-color: #68ad18;
  }
`;

const Meta = styled.div`
  font-family: Nunito Sans, sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: #899093;
  margin-top: 4px;
  /* Must stay one line: the panel's height is fixed so the legend can be
     offset beneath it, and a wrapped filename would overflow the card. */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 8px;
`;

const LinkButton = styled.button<{ danger?: boolean }>`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-family: Nunito Sans, sans-serif;
  font-size: 11px;
  font-weight: 700;
  color: ${({ danger }) => (danger ? "#e53e3e" : "#68ad18")};

  :disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const CustomLayerPanel: React.FC = () => {
  const vm = useFieldsScreenVM();
  const lang = useLang();
  const fieldsLang = lang.fields;
  const [confirming, setConfirming] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [draftName, setDraftName] = useState("");
  const [busy, setBusy] = useState(false);

  const layer = vm.activeCustomLayer;
  if (!vm.isCustomView(vm.selectedFieldView) || !layer || vm.selectedFieldId == null) {
    return null;
  }

  const fieldId = String(vm.selectedFieldId);

  const startRename = () => {
    setDraftName(layer.name);
    setConfirming(false);
    setRenaming(true);
  };

  const onRename = async () => {
    const name = draftName.trim();
    if (name === "" || name === layer.name) {
      setRenaming(false);
      return;
    }
    setBusy(true);
    try {
      await vm.renameCustomLayer(fieldId, layer.id, name);
      toast.success(fieldsLang.customLayerRenamed);
      setRenaming(false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async () => {
    setBusy(true);
    try {
      await vm.removeCustomLayer(fieldId, layer.id);
      toast.success(fieldsLang.customLayerDeleted);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  };

  const shapes = `${layer.featureCount} ${fieldsLang.customLayerShapes}`;
  const from =
    layer.source === "johndeere"
      ? fieldsLang.customLayerFromDeereShort
      : layer.sourceFilename ?? "";

  return (
    <Root>
      {renaming ? (
        <NameInput
          autoFocus
          value={draftName}
          disabled={busy}
          maxLength={80}
          onChange={(e) => setDraftName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") void onRename();
            if (e.key === "Escape") setRenaming(false);
          }}
        />
      ) : (
        <Name title={layer.name}>{layer.name}</Name>
      )}
      <Meta>
        {shapes}
        {from ? ` · ${from}` : ""}
        {!layer.hasValues
          ? ` · ${fieldsLang.customLayerOutlinesOnly}`
          : ""}
      </Meta>
      <Actions>
        {renaming ? (
          <>
            <LinkButton onClick={onRename} disabled={busy}>
              {fieldsLang.customLayerRenameSave}
            </LinkButton>
            <LinkButton onClick={() => setRenaming(false)} disabled={busy}>
              {fieldsLang.customLayerCancel}
            </LinkButton>
          </>
        ) : confirming ? (
          <>
            <LinkButton danger onClick={onDelete} disabled={busy}>
              {fieldsLang.customLayerDeleteConfirm}
            </LinkButton>
            <LinkButton onClick={() => setConfirming(false)} disabled={busy}>
              {fieldsLang.customLayerCancel}
            </LinkButton>
          </>
        ) : (
          <>
            <LinkButton onClick={startRename}>
              {fieldsLang.customLayerRename}
            </LinkButton>
            <LinkButton danger onClick={() => setConfirming(true)}>
              {fieldsLang.customLayerDelete}
            </LinkButton>
          </>
        )}
      </Actions>
    </Root>
  );
};

export default observer(CustomLayerPanel);
